import { config } from 'config';
import useApi from 'hooks/useApi';
import useAuthToken from 'hooks/useAuthToken';
import useStudentTutor from 'hooks/useStudentTutor';
import { Avatar } from 'primereact/avatar';
import { Skeleton } from 'primereact/skeleton';
import { memo, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import redirectToAuth from 'utilities/redirectToAuth';
import toNameInitials from 'utilities/toNameInitials';
import { AxiosError } from 'axios';
import { BackendError } from 'models/backend-error';

interface ComponentProps {
  tutorId: string;
}

const StudentTutorAvatar = ({ tutorId }: PropsWithChildren<ComponentProps>) => {
  const [ loading, setLoading ] = useState<boolean>(false);
  const [ avatar, setAvatar ] = useState<string | undefined>();
  const [ label, setLabel ] = useState<string | undefined>();

  const tutor = useStudentTutor({ tutorId });

  const { getAuthToken, removeAuthToken } = useAuthToken();
  const authToken = useMemo<string | null>(() => getAuthToken() ?? null, [ getAuthToken ]);

  const api = useApi();

  useEffect(() => {
    if (authToken) {
      setLoading(true);

      api.request<Blob>({
        method: 'GET',
        url: `student/tutors/${tutorId}/picture`,
        responseType: 'blob',
        headers: {
          authorization: `Bearer ${authToken}`,
        },
      }).then(({ data }) => {
        if (data.size) {
          return new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.readAsDataURL(data);

            reader.onloadend = () => {
              resolve(reader.result as string);
            };
          });
        } else {
          return Promise.resolve(null);
        }
      }).then((avatar) => {
        setLoading(false);

        if (avatar) {
          setAvatar(avatar);
        }
      }).catch((error: AxiosError<BackendError>) => {
        setLoading(false);

        if (error.response?.status === 401) {
          removeAuthToken();
          redirectToAuth();
        }
      });
    } else {
      redirectToAuth();
    }
  }, [ authToken, tutorId ]);

  useEffect(() => {
    if (tutor) {
      setLabel(toNameInitials(tutor.name));
    }
  }, [ tutor ]);

  const avatarClickHandler = useCallback(() => {
    window.open(`${config.appDomain}/dashboard/student/tutor/${tutorId}/details`, '_blank');
  }, [ tutorId ]);

  if (loading) {
    return (
      <Skeleton shape="circle" size="6rem" className="rounded-full"/>
    );
  } else if (avatar) {
    return (
      <Avatar onClick={avatarClickHandler} image={avatar} shape="circle" size="xlarge" style={{ width: '100px', height: '100px', 'borderRadius': '50%' }}/>
    );
  } else {
    return (
      <Avatar shape="circle" size="xlarge" icon="pi pi-user" label={label} style={{ width: '100px', height: '100px', 'borderRadius': '50%' }}/>
    );
  }
};

export default memo(StudentTutorAvatar, (prevProps, nextProps) => {
  return prevProps.tutorId === nextProps.tutorId;
});